import React from "react";

import { IFieldData } from "@Components/shared/types";
import { FrownFilled, MehFilled, SmileFilled, StarFilled } from "@ant-design/icons-svg";
import type { IconDefinition } from "@ant-design/icons-svg/lib/types";
import {
    Text,
    Path,
    Svg,
    View,
    Circle,
    Defs,
    Ellipse,
    G,
    Line,
    Polygon,
    Polyline,
    Rect,
    ClipPath,
    LinearGradient,
    RadialGradient,
    Stop,
    Tspan,
} from "@react-pdf/renderer";

/**
 * Converts a type to a pdf component
 */
export const convertFieldToPdfComponent = ({ options, type, value, style }: IFieldData) => {
    const baseText = (value: string) => <Text style={style}>{value}</Text>;
    switch (type) {
        case "text":
        case "textarea":
        case "option":
            return baseText(value);
        case "numeric":
            const isPercentage = options[2] === "true";
            return baseText(`${value}${isPercentage ? "%" : ""}`);

        case "boolean":
            return baseText(value ? "Yes" : "No");

        case "likert":
            switch (options[5]) {
                default: // Options - radio buttons
                    return baseText(options[value]);

                case "2": // Rating
                    return (
                        <View style={{ flexDirection: "row" }}>
                            {[...Array(Number(value)).keys()].map(() => iconToPDFSVG(StarFilled, { fill: "#FADB14" }))}
                        </View>
                    );

                case "3": // Smileys
                    const smileys = [FrownFilled, FrownFilled, MehFilled, SmileFilled, SmileFilled];
                    return (
                        <View style={{ flexDirection: "row" }}>
                            {[...Array(Number(value)).keys()].map((key) =>
                                iconToPDFSVG(smileys[key], { fill: "#FADB14" })
                            )}
                        </View>
                    );
            }

        default:
            return baseText(value);
    }
};

const TAGS = [
    "circle",
    "clipPath",
    "defs",
    "ellipse",
    "g",
    "line",
    "linearGradient",
    "path",
    "polygon",
    "polyline",
    "radialGradient",
    "rect",
    "stop",
    "text",
    "tspan",
] as const;
type Tag = (typeof TAGS)[number];

/**
 * Checks that string is a valid Tag
 */
const isTag = (tag: unknown): tag is Tag => {
    return typeof tag === "string" ? TAGS.map<string>((t) => t).includes(tag) : false;
};

/**
 * Returns an SVG element based on the Tag
 */
const identifySVGElementByTag = (tag: Tag) => {
    switch (tag) {
        case "circle":
            return Circle;
        case "clipPath":
            return ClipPath;
        case "defs":
            return Defs;
        case "ellipse":
            return Ellipse;
        case "g":
            return G;
        case "line":
            return Line;
        case "linearGradient":
            return LinearGradient;
        case "path":
            return Path;
        case "polygon":
            return Polygon;
        case "polyline":
            return Polyline;
        case "radialGradient":
            return RadialGradient;
        case "rect":
            return Rect;
        case "stop":
            return Stop;
        case "text":
            return Text;
        case "tspan":
            return Tspan;
        default:
            return null;
    }
};

/**
 * Converts an icon to a pdf component
 */
export const iconToPDFSVG = ({ icon }: IconDefinition, options: { fill?: string } = {}) => {
    if (typeof icon === "function") return null;

    return (
        <Svg {...icon.attrs} width={"20em"} height={"20em"}>
            {icon.children.map(({ attrs, tag }, index) => {
                if (!isTag(tag)) return null;
                const SVGElement = identifySVGElementByTag(tag);
                if (!SVGElement) return null;
                return <SVGElement {...attrs} {...options} key={`${tag}_${index}`} />;
            })}
        </Svg>
    );
};
